/**
 * VenueFlow Login Page
 * 
 * Beautiful, branded login page with:
 * - Email/password authentication
 * - Sign up flow
 * - Password reset flow
 * 
 * Falls back to allowing access if Supabase isn't configured.
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { isSupabaseConfigured } from "../lib/supabase";

// ============================================================
// THEME (matching AppShell)
// ============================================================
const THEME = {
  bg: "#0a0a0f",
  bgElevated: "#12121a",
  bgCard: "#16161f",
  bgHover: "#1e1e2a",
  accent: "#2fd6b5",
  accentHover: "#26b89a",
  accentGlow: "rgba(47, 214, 181, 0.15)",
  lime: "#cdde25",
  textPrimary: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  border: "rgba(255, 255, 255, 0.08)",
  borderHover: "rgba(255, 255, 255, 0.15)",
  gradientAccent: "linear-gradient(135deg, #2fd6b5 0%, #cdde25 100%)",
  shadowLg: "0 8px 32px rgba(0, 0, 0, 0.5)",
  shadowGlow: "0 0 60px rgba(47, 214, 181, 0.2)",
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: THEME.bg,
    padding: 24,
    position: "relative",
    overflow: "hidden",
  },
  backgroundGlow: {
    position: "absolute",
    top: "30%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    height: 600,
    background: "radial-gradient(ellipse, rgba(47, 214, 181, 0.08) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    background: THEME.bgElevated,
    borderRadius: 20,
    padding: "48px 40px",
    width: "100%",
    maxWidth: 420,
    boxShadow: THEME.shadowLg,
    border: `1px solid ${THEME.border}`,
  },
  logoContainer: {
    textAlign: "center",
    marginBottom: 32,
  },
  logo: {
    height: 36,
    marginBottom: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: THEME.textSecondary,
    fontFamily: "'Space Grotesk', sans-serif",
    lineHeight: 1.5,
  },
  
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: THEME.textSecondary,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  input: {
    padding: "14px 16px",
    fontSize: 15,
    background: THEME.bgCard,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  inputFocus: {
    borderColor: THEME.accent,
    boxShadow: `0 0 0 3px ${THEME.accentGlow}`,
  },
  button: {
    padding: "14px 24px",
    fontSize: 15,
    fontWeight: 600,
    background: THEME.gradientAccent,
    color: THEME.bg,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    transition: "transform 0.15s, box-shadow 0.2s",
    marginTop: 8,
  },
  buttonHover: {
    transform: "translateY(-1px)",
    boxShadow: THEME.shadowGlow,
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    transform: "none",
  },
  secondaryButton: {
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 500,
    background: "transparent",
    color: THEME.textSecondary,
    border: `1px solid ${THEME.border}`,
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "'Space Grotesk', sans-serif",
    transition: "all 0.2s ease",
  },
  successMessage: {
    textAlign: "center",
    padding: "24px 20px",
    background: THEME.accentGlow,
    borderRadius: 12,
    border: "1px solid rgba(47, 214, 181, 0.3)",
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  successTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: THEME.accent,
    marginBottom: 8,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  successText: {
    fontSize: 14,
    color: THEME.textSecondary,
    lineHeight: 1.6,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  error: {
    padding: "12px 16px",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 8,
    color: "#f87171",
    fontSize: 13,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  footer: {
    marginTop: 24,
    textAlign: "center",
    fontSize: 12,
    color: THEME.textMuted,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  footerLink: {
    color: THEME.accent,
    textDecoration: "none",
  },
  signUpPrompt: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 13,
    color: THEME.textSecondary,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  signUpLink: {
    color: THEME.accent,
    textDecoration: "none",
    cursor: "pointer",
    marginLeft: 4,
  },
  forgotPassword: {
    textAlign: "right",
    marginTop: -8,
  },
  forgotPasswordLink: {
    fontSize: 12,
    color: THEME.textMuted,
    textDecoration: "none",
    cursor: "pointer",
    transition: "color 0.15s",
  },
};

// ============================================================
// LOGIN PAGE COMPONENT
// ============================================================
export function LoginPage() {
  const { 
    signInWithPassword, 
    signUp,
    completePasswordReset,
    isPasswordRecovery,
    clearPasswordRecovery,
    userEmail,
  } = useAuth();
  
  // Auth mode: 'password' | 'signup' | 'forgot' | 'reset'
  const [authMode, setAuthMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  
  // Focus states
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);

  // Handle password recovery mode from URL
  useEffect(() => {
    if (isPasswordRecovery) {
      setAuthMode("reset");
      if (userEmail) setEmail(userEmail);
    }
  }, [isPasswordRecovery, userEmail]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInWithPassword(email, password);
      // Success - AuthContext will update and redirect
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await signUp(email, password);
      // With email confirmation disabled, user is automatically logged in
      // AuthContext will detect the session and redirect to app
      if (data?.user && !data?.session) {
        // Email confirmation is still enabled - show old message
        setSignUpSuccess(true);
      }
      // If session exists, user is logged in - AuthContext handles redirect
    } catch (err) {
      // Supabase returns "Signups not allowed for this instance" when the
      // signup endpoint is disabled in the project settings. That's the
      // intended state for VenueFlow — accounts are provisioned by Lumen8,
      // not self-service. Catch this specific error and replace it with a
      // friendlier message that tells the user what to do next.
      const raw = err?.message || "";
      if (/signups? not allowed/i.test(raw) || /signup.*disabled/i.test(raw)) {
        setError("To sign up as a new user, please contact your Lumen8 contact for support.");
      } else {
        setError(raw || "Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      await completePasswordReset(password);
      setResetComplete(true);
    } catch (err) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // If Supabase isn't configured, show a message
  if (!isSupabaseConfigured()) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundGlow} />
        <div style={styles.card}>
          <div style={styles.logoContainer}>
            <img src="/lumen8-logo.png" alt="Lumen8" style={styles.logo} />
            <h1 style={styles.title}>VenueFlow</h1>
            <p style={styles.subtitle}>
              Authentication is not configured yet.
            </p>
          </div>
          <div style={{ ...styles.error, background: "rgba(251, 191, 36, 0.1)", borderColor: "rgba(251, 191, 36, 0.3)", color: "#fbbf24" }}>
            Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to enable login.
          </div>
        </div>
      </div>
    );
  }

  // Sign up success state
  if (signUpSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundGlow} />
        <div style={styles.card}>
          <div style={styles.logoContainer}>
            <img src="/lumen8-logo.png" alt="Lumen8" style={styles.logo} />
          </div>
          <div style={styles.successMessage}>
            <div style={styles.successIcon}>🎉</div>
            <div style={styles.successTitle}>Account created!</div>
            <div style={styles.successText}>
              Please check your email at<br />
              <strong style={{ color: THEME.textPrimary }}>{email}</strong>
              <br /><br />
              Click the confirmation link to activate your account.
            </div>
          </div>
          <button
            onClick={() => {
              setSignUpSuccess(false);
              setAuthMode("password");
              setPassword("");
              setConfirmPassword("");
            }}
            style={{
              ...styles.secondaryButton,
              marginTop: 20,
              width: "100%",
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // Password reset complete success state
  // Note: User is already authenticated after password reset
  if (resetComplete) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundGlow} />
        <div style={styles.card}>
          <div style={styles.logoContainer}>
            <img src="/lumen8-logo.png" alt="Lumen8" style={styles.logo} />
          </div>
          <div style={styles.successMessage}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>Password Changed Successfully!</div>
            <div style={styles.successText}>
              Your password has been updated.<br />
              Click below to continue to VenueFlow.
            </div>
          </div>
          <button
            onClick={() => {
              // User is already authenticated, just clear the state
              // This will trigger re-render and show the main app
              setResetComplete(false);
              setPassword("");
              setConfirmPassword("");
              // Force a page reload to ensure clean state
              window.location.href = "/";
            }}
            style={{
              ...styles.button,
              marginTop: 20,
              width: "100%",
            }}
          >
            OK - Continue to VenueFlow
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundGlow} />
      <div style={styles.card}>
        {/* Logo & Header */}
        <div style={styles.logoContainer}>
          <img src="/lumen8-logo.png" alt="Lumen8" style={styles.logo} />
          <h1 style={styles.title}>
            {authMode === "reset" ? "Reset Password" : 
             authMode === "forgot" ? "Forgot Password" : 
             "Welcome to VenueFlow"}
          </h1>
          <p style={styles.subtitle}>
            {authMode === "signup" 
              ? "Create your account to get started"
              : authMode === "forgot"
              ? "Enter your email to receive a reset link"
              : authMode === "reset"
              ? "Enter your new password below"
              : "Sign in to manage your venue content"
            }
          </p>
        </div>

        {/* Password Login Form */}
        {authMode === "password" && (
          <form onSubmit={handlePasswordLogin} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                placeholder="you@yourclub.com.au"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(emailFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="••••••••"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(passwordFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            {/* Forgot Password Link */}
            <div style={styles.forgotPassword}>
              <span
                style={styles.forgotPasswordLink}
                onClick={() => { setAuthMode("forgot"); setError(""); setPassword(""); }}
                onMouseEnter={(e) => e.currentTarget.style.color = THEME.accent}
                onMouseLeave={(e) => e.currentTarget.style.color = THEME.textMuted}
              >
                Forgot password?
              </span>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading || !email || !password}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              style={{
                ...styles.button,
                ...(buttonHovered && !loading ? styles.buttonHover : {}),
                ...(loading || !email || !password ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>

            <div style={styles.signUpPrompt}>
              Don't have an account?
              <span 
                style={styles.signUpLink}
                onClick={() => { setAuthMode("signup"); setError(""); }}
              >
                Sign up
              </span>
            </div>
          </form>
        )}

        {/* Sign Up Form */}
        {authMode === "signup" && (
          <form onSubmit={handleSignUp} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                placeholder="you@yourclub.com.au"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(emailFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="At least 6 characters"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(passwordFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                placeholder="••••••••"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(confirmFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading || !email || !password || !confirmPassword}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              style={{
                ...styles.button,
                ...(buttonHovered && !loading ? styles.buttonHover : {}),
                ...(loading || !email || !password || !confirmPassword ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>

            <div style={styles.signUpPrompt}>
              Already have an account?
              <span 
                style={styles.signUpLink}
                onClick={() => { setAuthMode("password"); setError(""); setConfirmPassword(""); }}
              >
                Sign in
              </span>
            </div>
          </form>
        )}

        {/* Forgot Password - Contact Message */}
        {authMode === "forgot" && (
          <div style={styles.form}>
            <div style={{
              background: 'rgba(47, 214, 181, 0.1)',
              border: '1px solid rgba(47, 214, 181, 0.3)',
              borderRadius: 12,
              padding: '24px 20px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>📞</div>
              <p style={{
                color: '#fff',
                fontSize: 15,
                margin: '0 0 8px',
                fontWeight: 500,
              }}>
                Need to reset your password?
              </p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: 14,
                margin: 0,
                lineHeight: 1.5,
              }}>
                Please contact your<br />
                <strong style={{ color: '#2fd6b5' }}>Lumen8 Production Coordinator</strong><br />
                and they will reset it for you.
              </p>
            </div>

            <div style={styles.signUpPrompt}>
              Remember your password?
              <span 
                style={styles.signUpLink}
                onClick={() => { setAuthMode("password"); setError(""); }}
              >
                Sign in
              </span>
            </div>
          </div>
        )}

        {/* Reset Password Form (shown after clicking reset link) */}
        {authMode === "reset" && (
          <form onSubmit={handleResetPassword} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="At least 6 characters"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(passwordFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                placeholder="••••••••"
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(confirmFocused ? styles.inputFocus : {}),
                }}
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              style={{
                ...styles.button,
                ...(buttonHovered && !loading ? styles.buttonHover : {}),
                ...(loading || !password || !confirmPassword ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <div style={styles.signUpPrompt}>
              <span 
                style={styles.signUpLink}
                onClick={() => { 
                  clearPasswordRecovery(); 
                  setAuthMode("password"); 
                  setError(""); 
                  setPassword("");
                  setConfirmPassword("");
                }}
              >
                Cancel and go back to login
              </span>
            </div>
          </form>
        )}

        <div style={styles.footer}>
          Secure authentication powered by{" "}
          <a
            href="https://supabase.com"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.footerLink}
          >
            Supabase
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
